import Vue from 'vue';
import notifyStatus from '@/utils/notify-status';

export { notifyStatus };

export async function dispatchWaitAndNotify(action, data, noWait = false, noNotify = false) {
    if (!noWait) this.$wait.start(action);

    try {
        const response = await this.$store.dispatch(action, data)
        if (!noNotify) {
            this.notifyStatus(response.status);
        }
        Vue.prototype.loadedActions.push(action);
        return response.data;
    } catch (error) {
        this.notifyStatus(error.response.status, null, null, error.response);
        return Promise.reject(error);
    } finally {
        if (!noWait) {
            this.$wait.end(action);
        }
    }
}

export function validateFields(fields) { // [{value,name,rules}]
    const validators = {
        invalid_required(value, name) {
            return value ? false : `${name} is required.`;
        }, invalid_posInt(value, name) {
            return Number.isInteger(+value) && value >= 0 ? false : `${name} must be a number.`;
        }, invalid_N(value, name) {
            return Number.isInteger(+value) && value > 0 ? false : `${name} must be greater than 0.`;
        }, invalid_posFloat(value, name) {
            return Number.parseFloat(value) > 0 ? false : `${name} must be greater than 0.`;
        }, invalid_minValue(value, name, param) {
            return Number.parseFloat(value) >= parseFloat(param) ? false : `${name} must be at least ${param}.`;
        }, invalid_maxValue(value, name, param) {
            return Number.parseFloat(value) <= parseFloat(param) ? false : `${name} must be at most ${param}.`;
        }, invalid_minLength(value, name, param) {
            return value.length >= parseInt(param) ? false : `${name} minimal length is ${param}.`;
        }, invalid_minLengthIf(value, name, param) {
            return (!value || value.length >= parseInt(param)) ? false : `${name} minimal length is ${param}.`;
        }, invalid_maxLength(value, name, param) {
            return value.length <= parseInt(param) ? false : `${name} maximal length is ${param}.`;
        }, invalid_email(value, name) {
            return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value) ? false : `${name} must be valid.`;
        }, invalid_domain(value, name) {
            return /^((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value) ? false : `${name} must be valid domain.`;
        }, invalid_slug(value, name) {
            return /^[A-Za-z0-9-]+$/.test(value) ? false : `${name} must only contain (A-Z, a-z, 0-9 and -).`;
        }, invalid_mustMatch(value, name, param) {
            return value == param ? false : `${name} must match.`;
        },
    };

    const errors = {};
    fields.forEach((field) => {
        const fieldErrors = [];
        for (const rule of field.rules) {
            let ruleParts = rule;
            if (typeof ruleParts !== 'object') ruleParts = rule.split(':');
            const error = ruleParts.length > 1 ? validators[`invalid_${ruleParts[0]}`](field.value, field.name, ruleParts[1]) : validators[`invalid_${ruleParts[0]}`](field.value, field.name);
            if (error) {
                fieldErrors.push(error);
                break;
            }
        }
        if (fieldErrors.length) {
            errors[field.name] = fieldErrors;
        }
    });
    return errors;
}

export function parsePagination(obj, fromQuery, pageParams) {
    function parseVal(val) {
        if (val === 'true') return true;
        if (val === 'false') return false;
        if (val === 'null') return null;
        return val;
    }

    let ret;
    if (fromQuery) { // parsing from url query (initial page data load)
        ret = {
            page: obj.page ? parseInt(obj.page) : 1, per_page: obj.per_page ? parseInt(obj.per_page) : pageParams.pageOptions.itemsPerPage,
        };
        if (obj.sort) {
            ret.sort = obj.sort;
            ret.direction = obj.direction;
        }
        if (pageParams.filters) { // parse query filters.
            for (const [key, value] of Object.entries(obj)) {
                if (Object.keys(pageParams.filters).includes(key)) {
                    if (pageParams.filters[key].type === 'single') {
                        ret[key] = parseVal(value);
                        pageParams.filters[key].val = parseVal(value);
                    } else { // multiselect
                        ret[key] = parseVal(value);
                        pageParams.filters[key].val = Array.isArray(value) ? parseVal(value) : [value]; // TODO parse array elems
                    }
                }
            }
        }

        if (obj.q) {
            ret.q = obj.q ? obj.q : '';
            pageParams.search.val = ret.q;
        }

        // set pagination from query
        pageParams.pageOptions.page = obj.page ? parseInt(obj.page) : 1;
        // pageParams.pageOptions.itemsPerPage = parseInt(obj.per_page ? obj.per_page : 10);
        pageParams.pageOptions.sortBy = obj.sort ? [obj.sort] : [];
        pageParams.pageOptions.sortDesc = obj.direction ? [obj.direction === 'desc'] : [];
    } else {
        ret = {page: obj.pageOptions.page ? obj.pageOptions.page : 1, per_page: obj.pageOptions.itemsPerPage};
        if (obj.pageOptions.sortBy.length) {
            ret.sort = obj.pageOptions.sortBy[0];
            ret.direction = obj.pageOptions.sortDesc[0] ? 'desc' : 'asc';
        }

        if (obj.search.val) {
            ret.q = obj.search.val.trim().toLowerCase();
        }

        if (obj.filters) { // parse query filters.
            for (const [key, value] of Object.entries(obj.filters)) {
                if (value && (value.type === 'single' || !value.type)) {
                    if (value.val !== null && value.val !== 'null') {
                        ret[key] = value.val;
                    }
                }
            }
        }
    }
    return ret;
}

export async function getPageHandler(params) {
    if (this.$wait.is(params.waitName)) return;
    if (params.noWait !== true) this.$wait.start(params.waitName);
    let pageQuery = {};
    if (params.isInit) {
        pageQuery = this.parsePagination(this.$route.query, true, params.pageParams);
    } else {
        pageQuery = this.parsePagination(params.pageParams);
        if (params.resetPagination) {
            pageQuery.page = 1;
            params.pageParams.pageOptions.page = 1;
        }
    }
    try {
        await this.dispatchWaitAndNotify(params.action, {...pageQuery, ...params.routeParams}, params.noWait === 'undefined' ? false : params.noWait, true).then(() => {
            if (!params.isInit && params.pushRoute) {
                this.$router.push({query: pageQuery}).catch((e) => {
                    console.error(e);
                });
            }
        });
        // if (params.isInit) {
        // 	this.dispatchWaitAndNotify(params.action, pageQuery, false, true);
        // } else if (params.pushRoute) {
        // 	this.$router.push({query: pageQuery}).catch(e => {});
        // }
    } catch (e) {
        console.log(e);
    } finally {
        this.$wait.end(params.waitName);
    }
}

export function itemsPerPageCookie(listID, defaultPerPage) {
    if (!defaultPerPage) defaultPerPage = 50;
    return Vue.$cookies.get(`WSPerPage-${listID}`) ? parseInt(Vue.$cookies.get(`WSPerPage-${listID}`)) : defaultPerPage;
}

export function demoCheck() {
    if (this.$store.state.demo.isDemo) {
        this.modals.registrationModal = true;
        return false;
    }
    return true;
}

export function proCheck(message = '') {
    if (this.$store.state.team.detail.plan!='pro') {
        this.$store.state.proBlockMessage = message;
        this.modals.registrationModal = true;
        return false;
    }
    return true;
}

export function isInViewport(element,topOffset = 0) {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= ((window.innerHeight || document.documentElement.clientHeight) + topOffset) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

export function convertHexToRGBA(color, opacity) {
    color = color.replace('#', '');
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
}
